import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import {DatePipe} from '@angular/common';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faChevronRight,
  faCircleCheck,
  faCircleXmark,
  faClipboardList,
  faClock,
  faUser
} from '@fortawesome/pro-solid-svg-icons';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {faSpinnerThird} from '@fortawesome/pro-duotone-svg-icons';
import {BicBadgeComponent, BicButtonComponent} from '@buildinginformationcloud/bic-ui-components';
import {SimpleCheck} from '../../../api';
import {CheckReportService} from '../../../services/checks/check-report.service';
import {SkeletonLoadingDirective} from '../../../directives/skeleton-loading.directive';
import {EmptyStateComponent} from '../../util/design/empty-state/empty-state.component';

/**
 * Die zuletzt ausgeführten Prüfläufe, je Lauf eine Karte über die volle Breite.
 *
 * Bewusst nur Felder aus `SimpleCheck`. Erfüllungsgrad, Dateianzahl und Prüfdauer liefert
 * `/workflows/check-results/as-simple-checks` nicht mit – sie stehen pro Lauf nur am vollständigen
 * `CheckResult` bzw. hinter einem Request je Workflow-Instanz. Deshalb trägt die Karte rechts den
 * Status statt eines Ergebnisses.
 */
@Component({
  selector: 'app-dashboard-check-list',
  templateUrl: './dashboard-check-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DatePipe,
    FaIconComponent,
    BicBadgeComponent,
    BicButtonComponent,
    SkeletonLoadingDirective,
    EmptyStateComponent
  ]
})
export class DashboardCheckListComponent implements AfterViewInit, OnDestroy {

  /** Alle Prüfläufe des Projekts – Auswahl und Sortierung passieren hier. */
  @Input() checks: SimpleCheck[] = [];
  @Input() loading = false;
  /** Überschrift innerhalb der Liste – entfällt, wenn sie im Kachelkopf steht. */
  @Input() showHeading = true;
  /** Karten in der schmalen Darstellung (Karte pro Zeile, zweizeilig). */
  @Input() maxRows = 5;
  /** Karten, sobald die Angaben in Spalten liegen – dann ist mehr Platz zum Durchscrollen. */
  @Input() maxRowsWide = 12;

  @Output() checkClick = new EventEmitter<SimpleCheck>();
  @Output() createCheck = new EventEmitter<void>();

  private checkReportService = inject(CheckReportService);

  /**
   * Ab dieser Breite verteilt sich eine Karte auf Spalten (Typ, Prüfer*in, Datum, Status), statt
   * die Angaben in eine zweite Zeile zu stapeln.
   */
  private readonly COLUMNS_MIN_WIDTH = 640;

  wide = false;

  protected readonly faChevronRight = faChevronRight;
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircleXmark = faCircleXmark;
  protected readonly faClipboardList = faClipboardList;
  protected readonly faClock = faClock;
  protected readonly faUser = faUser;
  protected readonly faSpinnerThird = faSpinnerThird;

  private resizeObserver?: ResizeObserver;

  constructor(private el: ElementRef, private zone: NgZone) {
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => this.zone.run(() => this.updateLayout()));
    this.resizeObserver.observe(this.el.nativeElement);
    // Einmal explizit messen, sobald das Layout steht: der Observer meldet sich erst, wenn der
    // Tab gerendert wird (im Hintergrund liefert der Browser keine Callbacks).
    setTimeout(() => this.updateLayout());
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  get latestChecks(): SimpleCheck[] {
    return this.checkReportService.latestRuns(this.checks, this.wide ? this.maxRowsWide : this.maxRows);
  }

  get hasChecks(): boolean {
    return this.latestChecks.length > 0;
  }

  getCheckName(check: SimpleCheck): string {
    return this.checkReportService.getCheckName(check);
  }

  getCheckTypeLabel(check: SimpleCheck): string {
    return this.checkReportService.getCheckTypeLabel(check);
  }

  getStatusLabel(check: SimpleCheck): string {
    return this.checkReportService.getStatusLabel(check);
  }

  getStatusBadgeType(check: SimpleCheck): 'success' | 'warning' | 'error' | 'info' {
    return this.checkReportService.getStatusBadgeType(check);
  }

  getStatusIcon(check: SimpleCheck): IconDefinition {
    switch (this.checkReportService.getStatus(check)) {
      case 'FAILED':
        return faCircleXmark;
      case 'RUNNING':
        return faSpinnerThird;
      default:
        return faCircleCheck;
    }
  }

  getStatusIconClass(check: SimpleCheck): string {
    switch (this.checkReportService.getStatus(check)) {
      case 'FAILED':
        return 'text-red';
      case 'RUNNING':
        return 'text-yellow';
      default:
        return 'text-green';
    }
  }

  onCheckClick(check: SimpleCheck): void {
    this.checkClick.emit(check);
  }

  private updateLayout(): void {
    this.wide = this.el.nativeElement.clientWidth >= this.COLUMNS_MIN_WIDTH;
  }
}
